import React, { memo, useState, useMemo } from "react";
import VisitCard from "@/components/Patient/VisitCard";
import { Button } from "@/components/ui/button";
import { Prescription } from "@/types/prescription";
import { 
  Calendar, 
  FileText, 
  Clock,
  SortDesc
} from "lucide-react";
import { cn } from "@/lib/utils";
import { isValid } from "date-fns";

// Visit interface that contains prescription data
interface Visit {
  id: string;
  date: string;
  prescription?: Prescription;
  appointment?: any;
}

interface VisitTimelineProps {
  visits: Visit[];
  onEditPrescription?: (prescription: Prescription) => void;
  onViewPrescription?: (prescription: Prescription) => void;
  onCopyPrescription?: (prescription: Prescription) => void;
  onAddPrescription?: () => void;
  showActions?: boolean;
  loading?: boolean;
  isCreatingPrescription?: boolean;
}

// Safe date parsing utility
const parseDate = (dateString: string): Date | null => {
  try {
    const date = new Date(dateString);
    return isValid(date) ? date : null;
  } catch {
    return null;
  }
};

// Safe prescription validation
const isValidPrescription = (prescription: Prescription | undefined): prescription is Prescription => {
  return !!(
    prescription?.id && 
    prescription?.date && 
    parseDate(prescription.date)
  );
};

function VisitTimeline({ 
  visits, 
  onEditPrescription,
  onViewPrescription,
  onCopyPrescription,
  onAddPrescription, 
  showActions = true,
  loading = false,
  isCreatingPrescription = false
}: VisitTimelineProps) {
  
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

  // Simple filter and sort visits with prescriptions
  const sortedVisits = useMemo(() => {
    try {
      // Only include visits that have valid prescriptions
      const visitsWithPrescriptions = visits.filter((visit): visit is Visit & { prescription: Prescription } => 
        isValidPrescription(visit.prescription)
      );

      // Sort by date
      return visitsWithPrescriptions.sort((a, b) => {
        const dateA = parseDate(a.prescription.date);
        const dateB = parseDate(b.prescription.date);
        
        // Handle invalid dates
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        const timeA = dateA.getTime();
        const timeB = dateB.getTime();
        
        return sortOrder === 'desc' ? timeB - timeA : timeA - timeB;
      });
    } catch (error) {
      console.error('Error filtering visits:', error);
      return [];
    }
  }, [visits, sortOrder]);

  // Simple count
  const totalPrescriptions = sortedVisits.length;

  // Loading state - Using your CSS classes
  if (loading) {
    return (
      <div className="w-full">
        <div className="flex justify-between items-center mb-6">
          <div className="h-6 bg-muted rounded w-48 animate-pulse"></div>
          <div className="flex gap-2">
            <div className="h-8 bg-muted rounded w-20 animate-pulse"></div>
            <div className="h-8 bg-muted rounded w-32 animate-pulse"></div>
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-muted/50 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  // Handle empty state - Using your CSS classes
  if (totalPrescriptions === 0) {
    return (
      <div className="w-full">
        {/* Empty State - Using your CSS classes */}
        <div className="text-center py-16 medical-card">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">No prescriptions recorded</h3>
          <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
            This patient hasn't had any prescriptions yet. Create their first prescription to get started.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header with Controls - Using your CSS classes */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded-full">
            {totalPrescriptions} {totalPrescriptions === 1 ? 'prescription' : 'prescriptions'}
          </span>
        </div>
        
        {/* Controls */}
        {showActions && (
          <div className="flex items-center gap-6">
            {/* Sort Toggle */}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="flex items-center gap-2"
            >
              <SortDesc className={cn(
                "w-4 h-4 transition-transform",
                sortOrder === 'asc' && "rotate-180"
              )} />
              {sortOrder === 'desc' ? 'Most Recent' : 'Oldest First'}
            </Button>
          </div>
        )}
      </div>

      {/* Timeline */}
      {sortedVisits.length > 0 ? (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary/60 via-border to-muted"></div>
          
          <div className="space-y-6">
            {sortedVisits.map((visit, index) => {
              const isLatest = index === 0 && sortOrder === 'desc';
              
              return (
                <div key={visit.prescription.id} className="relative">
                  {/* Timeline marker */}
                  <div className={cn(
                    "absolute left-3 top-6 w-6 h-6 border-2 rounded-full flex items-center justify-center shadow-sm z-10 bg-card",
                    isLatest 
                      ? "border-primary" 
                      : "border-muted-foreground/30"
                  )}>
                    {isLatest ? (
                      <Clock className="w-3 h-3 text-primary" />
                    ) : (
                      <Calendar className="w-3 h-3 text-muted-foreground" />
                    )}
                  </div>

                  {/* Visit card container */}
                  <div className="ml-12">
                    <VisitCard 
                      prescription={visit.prescription}
                      onEdit={onEditPrescription}
                      onView={onViewPrescription}
                      onCopy={onCopyPrescription}
                      isLatest={isLatest}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Timeline end marker */}
          <div className="absolute left-3 bottom-0 w-6 h-6 bg-muted border-2 border-border rounded-full flex items-center justify-center">
            <div className="w-2 h-2 bg-muted-foreground/50 rounded-full"></div>
          </div>
        </div>
      ) : (
        /* No search results - Using your CSS classes */
        <div className="text-center py-12 medical-card">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No prescriptions found</h3>
          <p className="text-muted-foreground mb-4">
            No prescription records available for this patient.
          </p>
        </div>
      )}
    </div>
  );
}

export default memo(VisitTimeline);